import type { AssessmentConfigRes } from "src/app/models/models";
import { buildFormObservationCodes } from "./competency-scoring-v2";
import type { IObservationRating } from "src/app/cbta-web/services/offline-rating-calculation.service";

/** Maneuver breakdown cell. Keep when isOriginal is not false and totalAttempt is omitted or > 0. */
export type ManeuverBreakdownObsCell = {
	isOriginal?: boolean;
	totalAttempt?: number;
};

export type ManeuverBreakdownByManeuver = Record<string, Record<string, ManeuverBreakdownObsCell>>;

/**
 * Mirror of BE `RatingCalculationTs1V4Strategy.resolvePreviewOriginalObsCodes`.
 * Collects observation codes that appear as predefined (original) in any maneuver.
 */
export function resolveOriginalObsCodesFromManeuverBreakdown(
	maneuverBreakdown?: ManeuverBreakdownByManeuver | null,
): Set<string> {
	const originalObsCodes = new Set<string>();
	for (const maneuverObs of Object.values(maneuverBreakdown ?? {})) {
		for (const [obsCode, obsData] of Object.entries(maneuverObs)) {
			if (obsData.isOriginal === false) continue;
			const totalAttempt = obsData.totalAttempt;
			if (totalAttempt === undefined || totalAttempt > 0) {
				originalObsCodes.add(obsCode);
			}
		}
	}
	return originalObsCodes;
}

/**
 * Live-draft equivalent when maneuverBreakdown is not materialized:
 * admin form OBs with attempt > 0 (BE `isOriginal = presetAttempt > 0`).
 */
export function resolveOriginalObsCodesFromAssessmentConfig(
	assessmentConfig: AssessmentConfigRes[] | null | undefined,
): Set<string> {
	const originalObsCodes = new Set<string>();
	for (const assessment of assessmentConfig ?? []) {
		for (const obs of assessment.observations ?? []) {
			if ((obs.attempt ?? 0) > 0) {
				originalObsCodes.add(obs.code);
			}
		}
	}
	return originalObsCodes;
}

/**
 * Applies BE v4 filtering: restrict step-1 predefined slots to original form codes.
 * Sign-off and preview both pass resolved originalObsCodes when available.
 */
export function applyV4PreviewPredefinedFilter(
	predefinedObs: IObservationRating[],
	originalObsCodes: Set<string>,
): { predefinedObs: IObservationRating[]; formObservationCodes: Set<string> } {
	const attemptFiltered = predefinedObs.filter((obs) => (obs.totalAttempt ?? 0) > 0);
	const effectivePredefined =
		originalObsCodes.size > 0
			? attemptFiltered.filter((obs) => originalObsCodes.has(obs.observationCode))
			: attemptFiltered;
	return {
		predefinedObs: effectivePredefined,
		formObservationCodes: buildFormObservationCodes(effectivePredefined),
	};
}
